import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Announcement } from '@interfaces/general';
import { useGetAnnouncement } from './CommunicationsModal/hooks/useGetAnnouncement';
import { CommunicationsModal } from './CommunicationsModal/CommunicationsModal';
import { CommunicationsBanner } from './CommunicationsBanner/CommunicationsBanner';

/**
 * Dispatcher único del feature de Anuncios.
 *
 * Hace UNA sola llamada a /v1/announcement/active y distribuye los anuncios
 * por tipo:
 *   - modal  → cola (uno a la vez), priorizando is_persistent=true primero
 *   - banner → stack vertical fijo debajo del header (sobre cualquier página)
 *   - card   → expuesto via AnnouncementCardList para montar en pages
 */

const sortModalQueue = (items: Announcement[]): Announcement[] => {
  // Persistentes primero, luego por orden de llegada del back.
  const persistent = items.filter((a) => a.is_persistent);
  const rest = items.filter((a) => !a.is_persistent);
  return [...persistent, ...rest];
};

export const CommunicationsCenter = () => {
  const { announcements, isFetching } = useGetAnnouncement();

  const modalQueue = useMemo(
    () => sortModalQueue(announcements.filter((a) => a.announcement_type === 'modal')),
    [announcements],
  );

  // Dismiss solo de sesión: la fuente de verdad es el back vía show_once /
  // user_response. Ver AnnouncementCardList para el racional.
  const [sessionDismissedBanners, setSessionDismissedBanners] = useState<Set<string>>(new Set());
  const dismissBannerForSession = useCallback((uuid: string) => {
    setSessionDismissedBanners((prev) => {
      if (prev.has(uuid)) return prev;
      const next = new Set(prev);
      next.add(uuid);
      return next;
    });
  }, []);

  const banners = useMemo(
    () => announcements.filter((a) => a.announcement_type === 'banner' && !sessionDismissedBanners.has(a.uuid)),
    [announcements, sessionDismissedBanners],
  );

  // Cola de modales — uno a la vez
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentModal = modalQueue[currentIndex];

  // Track del set de uuids ya procesado: evita re-abrir el modal cuando un
  // refetch regenera modalQueue con los mismos uuids (el usuario ya los vio o
  // descartó). Solo abrimos cuando llega una cola con uuids distintos.
  const lastQueueKeyRef = useRef<string>('');

  useEffect(() => {
    if (isFetching) return;
    if (modalQueue.length === 0) {
      lastQueueKeyRef.current = '';
      return;
    }
    const queueKey = modalQueue.map((m) => m.uuid).join(',');
    if (queueKey !== lastQueueKeyRef.current) {
      lastQueueKeyRef.current = queueKey;
      setCurrentIndex(0);
      setIsModalOpen(true);
    }
  }, [modalQueue, isFetching]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (currentIndex < modalQueue.length - 1) {
      setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        setIsModalOpen(true);
      }, 500);
    }
  };

  return (
    <>
      {currentModal && (
        <CommunicationsModal announcement={currentModal} isOpen={isModalOpen} onClose={handleCloseModal} />
      )}

      {banners.length > 0 && (
        <div
          // fixed debajo del header (pt-16/lg:pt-14 del Layout) y de la
          // SubscriptionStatusBar mobile cuando aplica. z-30 queda por
          // encima del contenido pero debajo del modal (z-50 nativo).
          className="fixed left-0 right-0 z-30 flex flex-col gap-1 top-16 lg:top-14"
        >
          {banners.map((banner) => (
            <CommunicationsBanner
              key={banner.uuid}
              announcement={banner}
              onDismiss={() => dismissBannerForSession(banner.uuid)}
              onRespond={() => dismissBannerForSession(banner.uuid)}
            />
          ))}
        </div>
      )}

      {/* Card list — expuesto via AnnouncementCardList (commit 3) */}
    </>
  );
};
